import { createContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import type { RecordModel } from 'pocketbase';
import pb from '../api/pb';
import type { EmailPreferences } from '../api/userPreferencesService';

export interface AuthContextType {
  user: RecordModel | null;
  isAdmin: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (
    email: string,
    password: string,
    passwordConfirm: string,
    emailPreferences?: Partial<EmailPreferences>,
  ) => Promise<void>;
  logout: () => void;
  requestPasswordReset: (email: string) => Promise<void>;
  changePassword: (
    currentPassword: string,
    newPassword: string,
    newPasswordConfirm: string,
  ) => Promise<void>;
  requestEmailChange: (newEmail: string) => Promise<void>;
  loginWithOAuth: (provider: string) => Promise<void>;
  oauthProviders: Set<string>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<RecordModel | null>(pb.authStore.record);
  const [isLoading, setIsLoading] = useState(true);
  const [oauthProviders, setOauthProviders] = useState<Set<string>>(new Set());
  const isAdmin = !!user && user['is_admin'] === true;

  useEffect(() => {
    const unsubscribe = pb.authStore.onChange((_token, record) => {
      setUser(record as RecordModel | null);
    });

    pb.collection('users')
      .listAuthMethods()
      .then((methods) => {
        const enabled = new Set(methods.oauth2.providers.map((p) => p.name));
        setOauthProviders(enabled);
      })
      .catch(() => {
        // Silently fail — button stays hidden
      })
      .finally(() => {
        setIsLoading(false);
      });

    return () => {
      unsubscribe();
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    await pb.collection('users').authWithPassword(email, password);
  }, []);

  const register = useCallback(
    async (
      email: string,
      password: string,
      passwordConfirm: string,
      emailPreferences?: Partial<EmailPreferences>,
    ) => {
      await pb
        .collection('users')
        .create({ email, password, passwordConfirm, ...emailPreferences });
      await pb.collection('users').authWithPassword(email, password);
    },
    [],
  );

  const logout = useCallback(() => {
    pb.authStore.clear();
  }, []);

  const requestPasswordReset = useCallback(async (email: string) => {
    await pb.collection('users').requestPasswordReset(email);
  }, []);

  const changePassword = useCallback(
    async (currentPassword: string, newPassword: string, newPasswordConfirm: string) => {
      if (!pb.authStore.record) throw new Error('Not authenticated');
      await pb.collection('users').update(pb.authStore.record.id, {
        oldPassword: currentPassword,
        password: newPassword,
        passwordConfirm: newPasswordConfirm,
      });
    },
    [],
  );

  const requestEmailChange = useCallback(async (newEmail: string) => {
    await pb.collection('users').requestEmailChange(newEmail);
  }, []);

  const loginWithOAuth = useCallback(async (provider: string) => {
    await pb.collection('users').authWithOAuth2({ provider });
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAdmin,
        isLoading,
        login,
        register,
        logout,
        requestPasswordReset,
        changePassword,
        requestEmailChange,
        loginWithOAuth,
        oauthProviders,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
